import React, { useState, useRef, useEffect } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatButton } from '@cometchat/chat-uikit-react';
import '../../styles/CometChatUserProfile/CometChatUserProfile.css';

// API Configuration - Use localhost for development
//const API_BASE = 'http://localhost/miracle-backend';
const API_BASE = 'https://miracle-connect.gratusinc.org/miracle-backend';

// Type definitions
interface ApiResponse {
  success: boolean;
  message?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    avatar: string;
    cometchat_uid: string;
    created_at: string;
  };
  error?: string;
}

interface UploadResponse {
  success: boolean;
  message?: string;
  data?: {
    url: string;
    filename: string;
    thumbnail_url?: string;
    user_updated: boolean;
    size: number;
    dimensions: number[];
  };
  error?: string;
}

interface CometChatUserResponse {
  success: boolean;
  data: {
    uid: string;
    name: string;
    avatar: string;
    status: string;
    metadata?: any;
  };
}

interface UserProfileEditProps {
  user: CometChat.User;
  onHide: () => void;
  onUserUpdated: (user: CometChat.User) => void;
}

const CometChatUserProfile: React.FC<UserProfileEditProps> = ({
  user,
  onHide,
  onUserUpdated
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [avatar, setAvatar] = useState(user.getAvatar() || '');
  const [userId, setUserId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(user.getAvatar() || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user data from backend when component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE}/get_user.php?cometchat_uid=${encodeURIComponent(user.getUid())}`);
        
        if (response.ok) {
          const userData = await response.json();
          if (userData.success && userData.user) {
            setFirstName(userData.user.first_name || '');
            setLastName(userData.user.last_name || '');
            setEmail(userData.user.email || '');
            setAvatar(userData.user.avatar || '');
            setPreviewUrl(userData.user.avatar || '');
            setUserId(userData.user.id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [user]);

  // Function to fetch latest user data from CometChat REST API
  const fetchUserFromCometChat = async (cometchatUid: string): Promise<CometChat.User | null> => {
    try {
      const response = await fetch(`${API_BASE}/get_cometchat_user.php?cometchat_uid=${encodeURIComponent(cometchatUid)}`);
      
      if (!response.ok) {
        return null;
      }

      const userData: CometChatUserResponse = await response.json();
      
      if (userData.success && userData.data) {
        const cometChatUser = new CometChat.User(userData.data.uid);
        cometChatUser.setName(userData.data.name);
        cometChatUser.setAvatar(userData.data.avatar);
        return cometChatUser;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to fetch from CometChat:', error);
      return null;
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAvatarUpload(file);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    setIsUploading(true);
    setError('');

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Create FormData for upload
      const formData = new FormData();
      formData.append('avatar', file);
      if (userId) {
        formData.append('user_id', userId.toString());
      }

      // Upload to your PHP backend
      const uploadResponse = await fetch(`${API_BASE}/upload_avatar.php`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload image: ${uploadResponse.status}`);
      }

      const responseText = await uploadResponse.text();
      
      let uploadData: UploadResponse;
      try {
        uploadData = JSON.parse(responseText) as UploadResponse;
      } catch (jsonError) {
        console.error('Upload response parsing error:', responseText);
        throw new Error('Server returned invalid response. Check console for details.');
      }

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to upload image');
      }
      
      if (!uploadData.data?.url) {
        throw new Error('No URL returned from upload');
      }
      
      // ✅ CLEAN FLOW: Just update the preview, no redundant CometChat calls
      const newAvatarUrl = uploadData.data.url;
      setAvatar(newAvatarUrl);
      setPreviewUrl(newAvatarUrl);

      // Optional: Refresh from CometChat after a delay to see if changes synced
      setTimeout(async () => {
        const latestUser = await fetchUserFromCometChat(user.getUid());
        if (latestUser && latestUser.getAvatar()) {
          setPreviewUrl(latestUser.getAvatar());
          setAvatar(latestUser.getAvatar());
        }
      }, 1500);

    } catch (err: any) {
      console.error('Avatar upload error:', err);
      setError(err.message || 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!userId) {
      setError('User ID not found. Please refresh and try again.');
      return;
    }

    setIsUpdating(true);
    setError('');

    try {
      // Update user via your PHP backend - this will sync with CometChat
      const response = await fetch(`${API_BASE}/update_user.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: userId,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          avatar: avatar,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      const responseText = await response.text();
      
      let data: ApiResponse;
      try {
        data = JSON.parse(responseText) as ApiResponse;
      } catch (jsonError) {
        console.error('Update response parsing error:', responseText);
        throw new Error('Server returned invalid response. Check console for details.');
      }
      
      if (data.success && data.user) {
        // Fetch the latest user data from CometChat to get the updated info
        const updatedCometChatUser = await fetchUserFromCometChat(user.getUid());
        
        if (updatedCometChatUser) {
          // Use the data from CometChat (which should have the latest info)
          onUserUpdated(updatedCometChatUser);
        } else {
          // Fallback to our backend data if CometChat fetch fails
          const fallbackUser = new CometChat.User(user.getUid());
          fallbackUser.setName(`${data.user.first_name} ${data.user.last_name}`);
          fallbackUser.setAvatar(data.user.avatar);
          onUserUpdated(fallbackUser);
        }
        
        // Close the modal
        onHide();
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  if (isLoading) {
    return (
      <div className="user-profile-edit__wrapper">
        <div className="user-profile-edit">
          <div className="user-profile-edit__header">
            <div className="user-profile-edit__header-title">Edit Profile</div>
            <div className="user-profile-edit__header-close" onClick={onHide}></div>
          </div>
          <div className="user-profile-edit__content">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading profile data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-edit__wrapper">
      <div className="user-profile-edit">
        <div className="user-profile-edit__header">
          <div className="user-profile-edit__header-title">Edit Profile</div>
          <div className="user-profile-edit__header-close" onClick={onHide}></div>
        </div>

        <div className="user-profile-edit__content">
          <div className="user-profile-edit__avatar-section">
            <div className="user-profile-edit__avatar-container">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="User Avatar"
                  className="user-profile-edit__avatar-image"
                />
              ) : (
                <div className="user-profile-edit__avatar-placeholder">
                  {firstName.charAt(0).toUpperCase() || lastName.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <div className="user-profile-edit__avatar-overlay" onClick={triggerFileUpload}>
                {isUploading ? (
                  <div className="user-profile-edit__loading">Uploading...</div>
                ) : (
                  <div className="user-profile-edit__camera-icon">📷</div>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div className="user-profile-edit__avatar-hint">
              Click to change avatar
            </div>
          </div>

          <div className="user-profile-edit__form">
            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">First Name</label>
              <input
                type="text"
                className="user-profile-edit__input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                disabled={isUpdating}
              />
            </div>

            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">Last Name</label>
              <input
                type="text"
                className="user-profile-edit__input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                disabled={isUpdating}
              />
            </div>

            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">Email</label>
              <input
                type="email"
                className="user-profile-edit__input user-profile-edit__input--disabled"
                value={email || 'Not available'}
                disabled
              />
            </div>

            <div className="user-profile-edit__input-group">
              <label className="user-profile-edit__label">CometChat UID</label>
              <input
                type="text"
                className="user-profile-edit__input user-profile-edit__input--disabled"
                value={user.getUid()}
                disabled
              />
            </div>
          </div>

          {error && (
            <div className="user-profile-edit__error">
              {error}
            </div>
          )}

          <div className="user-profile-edit__actions">
            <div className="user-profile-edit__cancel-wrapper">
              <CometChatButton
                text="Cancel"
                onClick={onHide}
                disabled={isUpdating}
              />
            </div>
            <div className="user-profile-edit__save-wrapper">
              <CometChatButton
                text={isUpdating ? 'Saving...' : 'Save Changes'}
                onClick={handleSave}
                disabled={isUpdating || isUploading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CometChatUserProfile;